import { useCallback, useContext, useEffect, useMemo, FC } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import { useApplication } from '@eneco/common/application';
import logger from '@eneco/common/log';
import RichText from '@eneco/components/RichText/RichText';
import { Notification, useSetNotification } from '@eneco/components-next/Notification/Notification';
import {
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberContactDetails,
  postEnecoBeXapiSiteApiV1PriceSimulation,
  PriceSimulationElectricityUsageDto,
  PriceSimulationFeaturesDto,
  PriceSimulationGasUsageDto,
  PriceSimulationInstallationDto,
  PriceSimulationRequest,
  PriceSimulationSolarPanelsProductionDto,
  putEnecoBeXapiSiteApiV1PriceSimulationByPriceSimulationId,
} from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { useRouter } from '@eneco/next';
import { useTracking } from '@eneco/tracking';
import { Fields } from '@sitecore/types/PriceSimulation';
import { useFormContext } from 'react-hook-form';
import { useSWRConfig } from 'swr';

import { FlowNavigationContext } from '../../../providers/FlowNavigationProvider';
import { buildNavigationHref } from '../../../utils/buildNavigationHref';
import { languageToCustomerLanguageDto } from '../../../utils/language';
import { useOrderId } from '../../order-management/hooks/useOrderId';
import { PriceSimulationContext } from '../context/PriceSimulationProvider';
import { METER_TYPE_OPTIONS, PriceSimulationFormSchema } from '../schemas/priceSimulationFormSchema';
import { BiDirectionalMeterStep } from '../steps/BiDirectionalMeterStep';
import { CurrentEnergyConsumptionQuestionStep } from '../steps/CurrentEnergyConsumptionQuestionStep';
import { CustomerTypeStep } from '../steps/CustomerTypeStep';
import { ElectricityConsumptionStep } from '../steps/ElectricityConsumptionStep';
import { EnergyTypeStep } from '../steps/EnergyTypeStep';
import { FlowSelectorStep } from '../steps/FlowSelectorStep';
import { GasConsumptionStep } from '../steps/GasConsumptionStep';
import { HeatingCheckStep } from '../steps/HeatingCheckStep';
import { HouseholdSizeStep } from '../steps/HouseholdSizeStep';
import { InstallationCheckStep } from '../steps/InstallationCheckStep';
import { MeterRegistersTypeCheckStep } from '../steps/MeterRegistersTypeCheckStep';
import { MeterTypeCheckStep } from '../steps/MeterTypeCheckStep';
import { PeakEnergyConsumptionStep } from '../steps/PeakEnergyConsumptionStep';
import { PostalCodeStep } from '../steps/PostalCodeStep';
import { PriceSimulationResultStep } from '../steps/priceSimulationResultStep/PriceSimulationResultStep';
import { PropertyTypeCheckStep } from '../steps/PropertyTypeCheckStep';
import { ProspectStep } from '../steps/ProspectStep';
import { SolarConsumptionStep } from '../steps/SolarConsumptionStep';
import { SolarInstallDateStep } from '../steps/SolarInstallDateStep';
import {
  activeHeaderIndexForStep,
  buildDynamicStepOrder,
  PriceSimulationStep,
  priceSimulationSteps,
  StepBuilderParams,
} from '../types/PriceSimulationSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';
import { loadPriceSimulationState, savePriceSimulationState } from '../utils/priceSimulationLocalStorage';
type PriceSimulationStepperFlowProps = {
  fields: Fields;
};

const PriceSimulationStepperFlow: FC<PriceSimulationStepperFlowProps> = ({ fields }) => {
  const { priceSimulationStep, setPriceSimulationStep } = useContext(PriceSimulationContext);
  const { subscribeTriggerOnPreviousClick, subscribeTriggerOnCloseClick, setActiveStepIndicator, setShowPreviousBtn } =
    useContext(FlowNavigationContext);
  const { handleSubmit, watch, getValues } = useFormContext<PriceSimulationFormSchema>();
  const { data: session } = useSession();
  const { activePath, query } = useRouter();
  const priceSimulationId = query.priceSimulationId as string;
  const orderId = useOrderId();
  const setNotification = useSetNotification();
  const { selectedAccount } = useSelfServiceAccount();
  const { language } = useApplication();
  const { mutate } = useSWRConfig();
  const { trackEventBE } = useTracking();

  const isProspect = watch('isProspect');
  const customerType = watch('customerType');
  const currentEnergyConsumptionQuestion = watch('currentEnergyConsumptionQuestion');
  const energyType = watch('energyType');
  const installationCheck = watch('installationCheck');
  const regionInBelgium = watch('regionInBelgium');
  const meterType = watch('meterType');

  const getStepBuilderParams = useCallback((): StepBuilderParams => {
    return {
      isProspect: isProspect ?? false,
      isSoho: customerType === 'SOHO',
      isLoggedIn: !!session,
      isConsumptionKnown: currentEnergyConsumptionQuestion ?? false,
      isAllowedConsumptionEstimationFlag: fields.dynamicProductFlow.allowConsumptionEstimationFlag.value ?? false,
      isGas: energyType === 'Gas' || energyType === 'Both',
      isElectricity: energyType === 'Electricity' || energyType === 'Both',
      hasSolarPanels: installationCheck?.includes('SolarPanels') ?? false,
      isRegionWalloon: regionInBelgium === 'Walloon',
      meterType: meterType ?? METER_TYPE_OPTIONS.DIGITAL,
    };
  }, [
    isProspect,
    customerType,
    session,
    currentEnergyConsumptionQuestion,
    fields.dynamicProductFlow.allowConsumptionEstimationFlag.value,
    energyType,
    installationCheck,
    regionInBelgium,
    meterType,
  ]);

  const dynamicStepOrder = useMemo(() => buildDynamicStepOrder(getStepBuilderParams()), [getStepBuilderParams]);
  const currentStepIndex = dynamicStepOrder.indexOf(priceSimulationStep ?? 'prospectCheck');

  // Helper function to update step with all side effects
  const updateStep = useCallback(
    (newStep?: PriceSimulationStep) => {
      // Cookie-based step validation logic
      let newStepUrlIndex = -1;
      let cookieStepIndex = -1;

      if (newStep) newStepUrlIndex = dynamicStepOrder.indexOf(newStep);
      const cookieStep = loadPriceSimulationState()?.priceSimulationStep;
      if (cookieStep) {
        cookieStepIndex = dynamicStepOrder.indexOf(cookieStep!);
      }
      if (cookieStepIndex === -1 || newStepUrlIndex === -1) {
        // If no step requested in url OR if no cookies, return to start (step 0) immediately
        newStep = dynamicStepOrder[0];
        setPriceSimulationStep(newStep);
      } else if (priceSimulationStep == null && cookieStep) {
        // If price simulation step is undefined, but but there is one in the cookie, go to the cookie step
        newStep = cookieStep;
        setPriceSimulationStep(newStep);
      } else if (newStepUrlIndex - cookieStepIndex <= 1) {
        // Correct flow the difference between cookie and requested is only 1 step.
        setPriceSimulationStep(newStep!);
      } else {
        // User is skipping steps in the form so we redirect to his last step.
        const activeStepInCookie = cookieStepIndex === -1 ? 0 : cookieStepIndex;
        newStep = dynamicStepOrder[cookieStepIndex];
        window.location.href = `${activePath}#${dynamicStepOrder?.[activeStepInCookie]}`;
        return;
      }

      // Update URL hash
      window.location.hash = `#${newStep}`;

      // Update header step indicator
      setActiveStepIndicator(activeHeaderIndexForStep.get(newStep ?? dynamicStepOrder[0]) ?? 0);

      // Update previous button visibility
      const newStepIndex = dynamicStepOrder.indexOf(newStep ?? dynamicStepOrder[0]);
      if (newStepIndex === 0) {
        setShowPreviousBtn(false);
      } else if (session && newStep === 'flowSelector') {
        setShowPreviousBtn(false);
      } else {
        setShowPreviousBtn(true);
      }
    },
    [
      setPriceSimulationStep,
      setActiveStepIndicator,
      setShowPreviousBtn,
      dynamicStepOrder,
      priceSimulationStep,
      activePath,
      session,
    ],
  );

  const { data } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberContactDetails,
    {
      path: {
        accountNumber: selectedAccount.crmAccountNumber,
      },
    },
    [`/accounts/${selectedAccount.crmAccountNumber}/contact/details`],
    session,
  );
  const accountData = unwrapData(data);

  const onSubmit = useCallback(
    async (data: PriceSimulationFormSchema) => {
      const meterType = data.meterType;
      const region = data.regionInBelgium;
      const hasSolarPanels = data.installationCheck?.includes('SolarPanels') ?? false;

      if (!data.currentEnergyConsumptionQuestion) {
        if (hasSolarPanels) {
          data.compensationType = 'Compensation';
        } else {
          data.compensationType = 'Offtake';
        }
      }

      if (meterType === 'analogMeter' && region === 'Walloon' && hasSolarPanels) {
        data.compensationType = 'Compensation';
      }

      if (data.energyType === 'Gas') {
        data.compensationType = 'Offtake';
      }

      type PartialPriceSimulationFeaturesDto = Partial<PriceSimulationFeaturesDto> & {
        installations: PriceSimulationInstallationDto[];
      };

      let features: PartialPriceSimulationFeaturesDto = { installations: [] };

      if (dynamicStepOrder.includes('installationCheck')) {
        features = {
          ...features,
          installations: (data.installationCheck ?? []).filter(
            (item): item is PriceSimulationInstallationDto => item !== 'None' && item !== undefined,
          ),
        };
      }
      if (dynamicStepOrder.includes('heatingCheck')) {
        features = { ...features, heatingType: data.heatingCheck };
      }
      if (dynamicStepOrder.includes('propertyTypeCheck')) {
        features = { ...features, housingType: data.propertyTypeCheck };
      }
      if (dynamicStepOrder.includes('houseHoldSize')) {
        features = { ...features, houseHoldSize: data.houseHoldSize };
      }
      if (hasSolarPanels) {
        features = { ...features, solarPanelInstallationDate: data.solarPanelInstallDate };
      }

      let solarPanelsProduction: Partial<PriceSimulationSolarPanelsProductionDto> = {};
      if (dynamicStepOrder.includes('solarConsumption')) {
        if (getValues('solarConsumptionChoice') === 'kva') {
          solarPanelsProduction = { ...solarPanelsProduction, installationTotalKva: data.installationTotalKva || 0 };
        }
        if (getValues('solarConsumptionChoice') === 'kwh' && getValues('meterSubTypeRegister') === 'Single') {
          solarPanelsProduction = { ...solarPanelsProduction, totalInjection: data.totalInjection || 0 };
        }
        if (getValues('solarConsumptionChoice') === 'kwh' && getValues('meterSubTypeRegister') === 'Multiple') {
          solarPanelsProduction = {
            ...solarPanelsProduction,
            peakHoursInjection: data.peakHoursInjection || 0,
            offPeakHoursInjection: data.offPeakHoursInjection || 0,
          };
        }
      }

      let electricityUsage: Partial<PriceSimulationElectricityUsageDto> = {};
      let peakPower: Pick<PriceSimulationElectricityUsageDto, 'peakPower' | 'takeAveragePeak'> = {
        peakPower: undefined,
        takeAveragePeak: false,
      };

      if (dynamicStepOrder.includes('meterRegistersTypeCheck')) {
        const meterType = getValues('meterSubTypeRegister'); // single or multiple

        if (getValues('hasExclusiveNightMeter')) {
          electricityUsage = {
            ...electricityUsage,
            singularMeterUsage: {
              ...electricityUsage.singularMeterUsage,
              //todo should be nullable when meter is multiple. Because there is no total usage when meter type is multiple, but now backend expects the property.
              totalUsage: data.electricityConsumption?.single || 0,
              exclusiveNightUsage: data.electricityConsumption?.exclusiveNight,
            },
          };
        } else {
          electricityUsage = {
            ...electricityUsage,
            singularMeterUsage: {
              ...electricityUsage?.singularMeterUsage,
              totalUsage: data.electricityConsumption?.single || 0,
            },
          };
        }

        if (meterType === 'Multiple') {
          electricityUsage = {
            ...electricityUsage,
            multipleMeterUsage: {
              peakUsage: data.electricityConsumption?.day || 0,
              offPeakUsage: data.electricityConsumption?.night || 0,
            },
          };
        }
      }

      let gas: PriceSimulationGasUsageDto | undefined = undefined;
      if (dynamicStepOrder.includes('gasConsumption')) {
        gas = {
          totalUsage: data.gasConsumption || 0,
        };
      }

      if (dynamicStepOrder.includes('peakEnergyConsumption')) {
        peakPower = {
          ...peakPower,
          peakPower: Number(data.peakEnergyConsumption?.replace(',', '.')) || 0,
          takeAveragePeak: data.isCheckedUseDefaultPeakConsumption ?? false,
        };
      }

      const meterSubType = () => {
        if (
          data.currentEnergyConsumptionQuestion &&
          (data.energyType === 'Both' || data.energyType === 'Electricity')
        ) {
          return data.meterSubTypeRegister;
        }
        return undefined;
      };

      const request: PriceSimulationRequest = {
        customerType: data.customerType || accountData?.customerType || 'Residential',
        usage: {
          electricity: {
            ...electricityUsage,
            ...peakPower,
            solarPanelsProduction: {
              ...solarPanelsProduction,
            },
          },
          ...(gas && { gas }),
        },
        isUsageKnown: Boolean(data.currentEnergyConsumptionQuestion),
        isUsingDomiciliation: Boolean(data.isUserDomiciledOnPostalCode),
        energyType: data.energyType || 'Electricity',
        isProspect: data.isProspect ?? false,
        postalCode: data.postalCode || '',
        city: data.city || '',
        accountNumber: selectedAccount.crmAccountNumber,
        features: {
          meterSubType: meterSubType(),
          compensationType: data.compensationType,
          hasExclusiveNightMeter: data.hasExclusiveNightMeter,
          meterType:
            data.meterType === 'analogMeter'
              ? 'Analogue'
              : data.meterType === 'biDirectionalMeter'
                ? 'BiDirectional'
                : 'Digital',
          ...features,
        },
        language: languageToCustomerLanguageDto(language),
      };

      try {
        let resultPriceSimulationId: string;

        if (priceSimulationId) {
          const { response } = await putEnecoBeXapiSiteApiV1PriceSimulationByPriceSimulationId({
            path: { priceSimulationId },
            body: request,
          });
          if (!response.ok) {
            throw new Error('Failed to update price simulation: ' + response.statusText);
          }
          mutate(`/price-simulation/${priceSimulationId}`);
          resultPriceSimulationId = priceSimulationId;
        } else {
          const { response, data: priceSimulationData } = await postEnecoBeXapiSiteApiV1PriceSimulation({
            body: request,
          });
          if (!response.ok) {
            throw new Error('Failed to create price simulation: ' + response.statusText);
          }
          resultPriceSimulationId = priceSimulationData?.data?.priceSimulationId ?? '';
          if (!resultPriceSimulationId) {
            throw new Error('Failed to get price simulation id from response');
          }
          mutate(`/price-simulation/${resultPriceSimulationId}`);
        }
        savePriceSimulationState({
          formValues: data,
          priceSimulationStep: 'priceSimulationResult',
        });
        window.location.href = buildNavigationHref(activePath, {
          queryParams: {
            priceSimulationId: resultPriceSimulationId,
            ...(orderId ? { orderId } : {}),
          },
          stepHash: priceSimulationSteps.priceSimulationResult,
        });
      } catch (e) {
        setNotification({
          title: fields.summaryStep.errorNotification.value.title,
          text: <RichText html={fields.summaryStep.errorNotification.value.content} />,
          variant: 'error',
        });
        logger.error('Failed to initiate price simulation', e instanceof Error ? e.message : String(e));
      }
    },
    [
      dynamicStepOrder,
      accountData?.customerType,
      selectedAccount.crmAccountNumber,
      language,
      getValues,
      priceSimulationId,
      orderId,
      activePath,
      mutate,
      setNotification,
      fields.summaryStep.errorNotification.value.title,
      fields.summaryStep.errorNotification.value.content,
    ],
  );

  const nextMainStepLogicManager = useCallback(() => {
    const nextStepIndex = currentStepIndex + 1;
    const nextStep = dynamicStepOrder[nextStepIndex];
    if (nextStep === 'priceSimulationResult') {
      handleSubmit(onSubmit)();
    } else if (nextStepIndex < dynamicStepOrder.length) {
      updateStep(nextStep);
      setNotification(null);
    } else {
      throw new Error('No next step found');
    }
  }, [currentStepIndex, dynamicStepOrder, updateStep, handleSubmit, onSubmit, setNotification]);

  const previousMainStepLogicManager = useCallback(() => {
    const previousStepIndex = currentStepIndex - 1;
    if (previousStepIndex >= 0) {
      updateStep(dynamicStepOrder[previousStepIndex]);
      setNotification(null);
    } else {
      throw new Error('No previous step found');
    }
  }, [currentStepIndex, dynamicStepOrder, updateStep, setNotification]);

  const previousMainStep = useCallback(
    (transition?: PriceSimulationStep) => {
      if (transition) updateStep(transition);
      else previousMainStepLogicManager();
    },
    [previousMainStepLogicManager, updateStep],
  );

  const nextMainStep = useCallback(
    (transition?: PriceSimulationStep) => {
      if (transition) updateStep(transition);
      else nextMainStepLogicManager();
    },
    [nextMainStepLogicManager, updateStep],
  );

  // Set step from URL hash
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      const urlStep = hash.slice(1) as PriceSimulationStep;

      if (dynamicStepOrder.includes(urlStep)) {
        // Use updateStep to ensure all side effects happen
        updateStep(urlStep);
      } else {
        updateStep();
      }
    };

    handleHashChange();
    // Listen for hash changes (browser navigation)
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [dynamicStepOrder, updateStep]);

  // Subscribe to previous button clicks
  useEffect(() => {
    return subscribeTriggerOnPreviousClick(previousMainStep);
  }, [subscribeTriggerOnPreviousClick, previousMainStep]);

  // Useeffect for tracking analytics - close and previous clicks
  useEffect(() => {
    const unsubscribeClose = subscribeTriggerOnCloseClick(() => {
      trackEventBE('pbt_click_close', {
        data: { ...getTrackableFormValues(getValues()), priceSimulationStep },
      });
    });

    const unsubscribePrevious = subscribeTriggerOnPreviousClick(() => {
      trackEventBE('pbt_click_previous', {
        data: { ...getTrackableFormValues(getValues()), priceSimulationStep },
      });
    });

    return () => {
      unsubscribeClose();
      unsubscribePrevious();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeTriggerOnCloseClick, subscribeTriggerOnPreviousClick]);

  const renderStep = useCallback(() => {
    switch (priceSimulationStep) {
      case 'prospectCheck':
        return <ProspectStep nextMainStep={nextMainStep} />;
      case 'customerTypeCheck':
        return <CustomerTypeStep nextMainStep={nextMainStep} />;
      case 'flowSelector':
        return <FlowSelectorStep nextMainStep={nextMainStep} />;
      case 'postalCodeCheck':
        return <PostalCodeStep nextMainStep={nextMainStep} />;
      case 'energyTypeCheck':
        return <EnergyTypeStep nextMainStep={nextMainStep} />;
      case 'currentEnergyConsumptionQuestion':
        return <CurrentEnergyConsumptionQuestionStep nextMainStep={nextMainStep} />;
      case 'installationCheck':
        return <InstallationCheckStep nextMainStep={nextMainStep} />;
      case 'heatingCheck':
        return <HeatingCheckStep nextMainStep={nextMainStep} />;
      case 'propertyTypeCheck':
        return <PropertyTypeCheckStep nextMainStep={nextMainStep} />;
      case 'houseHoldSize':
        return <HouseholdSizeStep nextMainStep={nextMainStep} />;
      case 'meterTypeCheck':
        return <MeterTypeCheckStep nextMainStep={nextMainStep} />;
      case 'gasConsumption':
        return <GasConsumptionStep nextMainStep={nextMainStep} />;
      case 'meterRegistersTypeCheck':
        return <MeterRegistersTypeCheckStep nextMainStep={nextMainStep} />;
      case 'solarInstallDate':
        return <SolarInstallDateStep nextMainStep={nextMainStep} />;
      case 'biDirectionalMeterStep':
        return <BiDirectionalMeterStep nextMainStep={nextMainStep} />;
      case 'electricityConsumption':
        return <ElectricityConsumptionStep nextMainStep={nextMainStep} />;
      case 'peakEnergyConsumption':
        return <PeakEnergyConsumptionStep nextMainStep={nextMainStep} />;
      case 'solarConsumption':
        return <SolarConsumptionStep nextMainStep={nextMainStep} />;
      case 'priceSimulationResult':
        return <PriceSimulationResultStep />;
      default:
        return null;
    }
  }, [nextMainStep, priceSimulationStep]);

  return (
    <>
      {renderStep()}
      <Notification />
    </>
  );
};

export default PriceSimulationStepperFlow;
